import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Check, X } from 'lucide-react';
import { personalAPI } from '../services/api';
import { invalidatePersonal } from '../services/cachedCatalogs';
import { useConfirm, Tooltip } from './ui';

const PersonalPanel = () => {
    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [personal, setPersonal] = useState([]);
    const [nuevoPersonal, setNuevoPersonal] = useState({ nombre: '', cargo: '', costo_hora: 0 });
    const [pending, setPending] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    const getVal = (p, field) => (
        pending[p.id]?.[field] !== undefined ? pending[p.id][field] : p[field] ?? ''
    );
    const setVal = (id, field, value) => {
        setPending(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };
    const hasChanges = (id) => Boolean(pending[id] && Object.keys(pending[id]).length > 0);
    const confirmChanges = async (id) => {
        const changes = pending[id];
        if (!changes) return;
        await actualizarPersonal(id, changes);
        setPending(prev => { const n = { ...prev }; delete n[id]; return n; });
        toast.success('Personal actualizado');
    };
    const discardChanges = (id) => {
        setPending(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    // Reset a pagina 1 al cambiar busqueda o tamaño de pagina
    useEffect(() => { setCurrentPage(1); }, [busqueda, pageSize]);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const res = await personalAPI.getAll(true);
            setPersonal(res.data || []);
        } catch (err) {
            console.error('Error al cargar personal:', err);
        } finally {
            setLoading(false);
        }
    };

    const agregarPersonal = async () => {
        if (!nuevoPersonal.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        try {
            await personalAPI.create(nuevoPersonal);
            invalidatePersonal();
            setNuevoPersonal({ nombre: '', cargo: '', costo_hora: 0 });
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarPersonal = async (id, data) => {
        try {
            await personalAPI.update(id, data);
            invalidatePersonal();
            setPersonal(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarPersonal = async (id, nombre) => {
        const ok = await confirm({
            title: '¿Eliminar personal?',
            message: <p>Vas a eliminar a <strong>{nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await personalAPI.delete(id);
            invalidatePersonal();
            toast.success('Personal eliminado');
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando personal...</div>;
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2>Personal</h2>
            </div>
            <div className="admin-content">
                <h3 className="h3">Agregar Personal</h3>
                <div className="servicios-form-grid">
                    <label className="field">
                        <span className="label">Nombre *</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoPersonal.nombre}
                            onChange={(e) => setNuevoPersonal(n => ({ ...n, nombre: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Cargo</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoPersonal.cargo}
                            onChange={(e) => setNuevoPersonal(n => ({ ...n, cargo: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Costo/hora</span>
                        <input
                            className="input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={nuevoPersonal.costo_hora}
                            onChange={(e) => setNuevoPersonal(n => ({ ...n, costo_hora: parseFloat(e.target.value) || 0 }))}
                        />
                    </label>
                    <button className="btn neon servicios-form-submit" type="button" onClick={agregarPersonal}>
                        + Agregar
                    </button>
                </div>

                <div className="divider" />

                {(() => {
                    const busquedaNorm = busqueda.trim().toLowerCase();
                    const personalFiltrado = personal.filter(p => {
                        if (!busquedaNorm) return true;
                        return String(p.nombre || '').toLowerCase().includes(busquedaNorm)
                            || String(p.cargo || '').toLowerCase().includes(busquedaNorm);
                    });
                    const totalPaginas = Math.max(1, Math.ceil(personalFiltrado.length / pageSize));
                    const paginaActual = Math.min(currentPage, totalPaginas);
                    const personalPagina = personalFiltrado.slice((paginaActual - 1) * pageSize, paginaActual * pageSize);
                    return (
                <>
                <div className="admin-section-header">
                    <h3 className="h3">Personal Existente ({personalFiltrado.length})</h3>
                    <div className="admin-filtros-inline">
                        <input
                            className="input admin-buscador-inline"
                            type="search"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por nombre o cargo..."
                            aria-label="Buscar personal"
                        />
                    </div>
                </div>
                <div className="admin-list">
                    {personalPagina.map(p => {
                        const dirty = hasChanges(p.id);
                        return (
                        <div key={p.id} className={`admin-item ${!p.activo ? 'inactive' : ''} ${dirty ? 'has-pending-changes' : ''}`}>
                            <div className="admin-item-content">
                                <input
                                    className="input"
                                    type="text"
                                    value={getVal(p, 'nombre')}
                                    onChange={(e) => setVal(p.id, 'nombre', e.target.value)}
                                />
                                <input
                                    className="input"
                                    type="text"
                                    value={getVal(p, 'cargo')}
                                    onChange={(e) => setVal(p.id, 'cargo', e.target.value)}
                                    placeholder="Cargo..."
                                />
                                <div className="field-inline">
                                    <span className="label">$/hr:</span>
                                    <input
                                        className="input input-sm"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={getVal(p, 'costo_hora')}
                                        onChange={(e) => setVal(p.id, 'costo_hora', parseFloat(e.target.value) || 0)}
                                    />
                                </div>
                            </div>
                            <div className="admin-item-actions">
                                {dirty && (
                                    <>
                                        <button
                                            className="btn success small"
                                            onClick={() => confirmChanges(p.id)}
                                        >
                                            <Check size={14} />
                                            Actualizar
                                        </button>
                                        <Tooltip content="Descartar cambios">
                                            <button
                                                className="btn ghost small"
                                                onClick={() => discardChanges(p.id)}
                                                aria-label="Descartar cambios"
                                            >
                                                <X size={14} />
                                            </button>
                                        </Tooltip>
                                    </>
                                )}
                                <button
                                    className={`btn ghost small ${p.activo ? '' : 'success'}`}
                                    onClick={() => actualizarPersonal(p.id, { activo: !p.activo })}
                                >
                                    {p.activo ? 'Desactivar' : 'Activar'}
                                </button>
                                <button
                                    className="btn ghost small danger"
                                    onClick={() => eliminarPersonal(p.id, p.nombre)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                        );
                    })}
                    {personalFiltrado.length === 0 && (
                        <p className="empty-message">
                            {busqueda ? 'Sin resultados para esa búsqueda' : 'No hay personal creado'}
                        </p>
                    )}
                </div>

                {/* Paginacion client-side — mismo patron que Servicios/CostosFijos */}
                {personalFiltrado.length > 0 && (
                    <div className="admin-pagination">
                        <div className="admin-pagination-info">
                            Mostrando <strong>{(paginaActual - 1) * pageSize + 1}</strong>
                            {' – '}
                            <strong>{Math.min(paginaActual * pageSize, personalFiltrado.length)}</strong>
                            {' de '}
                            <strong>{personalFiltrado.length}</strong>
                        </div>
                        <div className="admin-pagination-controls">
                            <label className="admin-pagination-size">
                                <span className="text-xs text-muted">Por página:</span>
                                <select
                                    className="input"
                                    value={pageSize}
                                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>
                            <div className="admin-pagination-buttons">
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === 1}
                                    onClick={() => setCurrentPage(1)}
                                >« Inicio</button>
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >‹</button>
                                <span className="admin-pagination-current">
                                    Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === totalPaginas}
                                    onClick={() => setCurrentPage(p => Math.min(totalPaginas, p + 1))}
                                >›</button>
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === totalPaginas}
                                    onClick={() => setCurrentPage(totalPaginas)}
                                >Fin »</button>
                            </div>
                        </div>
                    </div>
                )}
                </>
                    );
                })()}
            </div>
        </div>
    );
};

export default PersonalPanel;
